/**
 * Orchestration O1 — ouvrir un manager après mount/ref, sans setTimeout unique arbitraire.
 * Consomme le pending uniquement après open réussi (ou abandon).
 */

/**
 * @param {{
 *   isReady: () => boolean,
 *   open: () => void,
 *   onOpened?: () => void,
 *   onGiveUp?: () => void,
 *   intervalMs?: number,
 *   maxAttempts?: number,
 * }} opts
 * @returns {() => void} cancel
 */
export function scheduleOpenWhenReady({
  isReady,
  open,
  onOpened,
  onGiveUp,
  intervalMs = 50,
  maxAttempts = 40,
} = {}) {
  if (typeof isReady !== 'function' || typeof open !== 'function') {
    throw new Error('scheduleOpenWhenReady: isReady et open requis');
  }

  let attempts = 0;
  let cancelled = false;
  let timeoutId = null;
  let settled = false;

  const settleOpened = () => {
    if (settled) return;
    settled = true;
    onOpened?.();
  };

  const settleGiveUp = () => {
    if (settled) return;
    settled = true;
    onGiveUp?.();
  };

  const tick = () => {
    if (cancelled) return;
    attempts += 1;

    if (isReady()) {
      open();
      settleOpened();
      return;
    }

    if (attempts >= maxAttempts) {
      settleGiveUp();
      return;
    }

    timeoutId = setTimeout(tick, intervalMs);
  };

  timeoutId = setTimeout(tick, 0);

  return () => {
    cancelled = true;
    if (timeoutId != null) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };
}

/**
 * Décide si un pending create doit être orchestré pour l’onglet courant.
 * @param {{ pendingType: string|null|undefined, activeTab: string|null|undefined }} input
 * @returns {'prestation'|'produit'|null}
 */
export function resolvePendingCreateTarget({ pendingType, activeTab } = {}) {
  if (pendingType === 'prestation' && activeTab === 'prestations') {
    return 'prestation';
  }
  if (pendingType === 'produit' && activeTab === 'produits') {
    return 'produit';
  }
  return null;
}

/**
 * Décide si un pending edit Prestation doit être orchestré (post-conversion / catalogue).
 * @param {{ pendingEditId: string|null|undefined, activeTab: string|null|undefined }} input
 * @returns {string|null} prestationId
 */
export function resolvePendingEditTarget({ pendingEditId, activeTab } = {}) {
  if (!pendingEditId || activeTab !== 'prestations') return null;
  const id = String(pendingEditId).trim();
  return id || null;
}
